import { useEffect, useRef, useState } from 'react';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const courseTypes = ['成人個人課', '兒童個人課', '不確定，想先了解'];
const timeSlots = ['平日上午（10:00–12:00）', '平日下午（13:00–18:00）', '平日晚間（18:00–21:00）', '週六上午', '週六下午', '彈性配合'];

export default function TrialBookingForm() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [charCount, setCharCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const toggleTime = (slot: string) => {
    setSelectedTimes((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const form = e.currentTarget;
    const honeypot = form.querySelector('input[name="website_alt"]') as HTMLInputElement | null;
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      return;
    }
    const data = new FormData(form);
    data.delete('website_alt');

    // Inject multi-select values
    data.set('課程類型', selectedCourse);
    data.set('方便上課時間', selectedTimes.join('、') || '未填寫');

    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));

    setStatus('submitting');
    setErrorMsg('');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetch('https://readdy.ai/api/form/d7f7covimhg6tri2v6gg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        signal: controller.signal,
      });
      const responseText = await res.text();
      let code = '';
      let serverMsg = '';
      try {
        const parsed = JSON.parse(responseText);
        code = parsed?.code ?? '';
        serverMsg = parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? '';
      } catch {
        code = '';
      }
      if (res.ok && code === 'OK') {
        setStatus('success');
        setErrorMsg('');
        form.reset();
        setSelectedCourse('');
        setSelectedTimes([]);
        setCharCount(0);
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || '送出失敗，請稍後再試或直接加入 LINE@ 聯絡。');
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        setStatus('error');
        setErrorMsg('連線逾時，請稍後再試一次，或直接加入 LINE@ 聯絡。');
      } else {
        setStatus('error');
        setErrorMsg('網路連線異常，請稍後再試或直接加入 LINE@ 聯絡。');
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  return (
    <section id="trial-booking" className="scroll-mt-24 md:scroll-mt-28 py-20 md:py-28 bg-[#faf8f5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-6 h-px bg-[#b08060]"></div>
              <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Trial Lesson</span>
              <div className="w-6 h-px bg-[#b08060]"></div>
            </div>
            <h2
              className="font-serif-tc text-[#2c2420] leading-[1.3] mb-4"
              style={{ fontSize: 'clamp(1.625rem, 3vw, 2.25rem)' }}
            >
              預約半價體驗課
            </h2>
            <p className="text-[1.0625rem] leading-[1.75] text-[#5a4e42] max-w-xl mx-auto">
              填寫以下基本資料，我會在 24 小時內透過 LINE 或電話與您確認時間。體驗課以半價優惠提供，不需要任何承諾。
            </p>
          </div>

          {/* Success State */}
          {status === 'success' && (
            <div role="status" aria-live="polite" className="rounded-2xl bg-[#edf5f0] border border-[#b8d8c8] p-10 text-center mb-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#5a8a72]/20 mx-auto mb-5">
                <i className="ri-check-line text-2xl text-[#5a8a72]"></i>
              </div>
              <h3 className="font-serif-tc text-[#2c2420] text-xl mb-3">預約申請已送出！</h3>
              <p className="text-[#5a4e42] leading-[1.75]">
                感謝您的申請，我會在 24 小時內聯繫您確認體驗課時間。<br />
                如有任何問題，也歡迎直接加入 LINE@ 聯絡。
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-6 px-6 py-2.5 rounded-full border border-[#c8b49a] text-[#7a6248] text-sm font-medium hover:bg-[#f5f0e8] transition-all cursor-pointer whitespace-nowrap"
              >
                再次填寫
              </button>
            </div>
          )}

          {/* Form */}
          {status !== 'success' && (
            <form
              data-readdy-form
              id="trial-booking-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl border border-[#ede5d8] p-8 md:p-10 space-y-7"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-aux"
              />
              {/* Row 1: Name + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="trial-name" className="block text-[0.875rem] font-medium text-[#3a2e22] mb-2">
                    姓名 <span className="text-[#c0503a]">*</span>
                  </label>
                  <input
                    id="trial-name"
                    type="text"
                    name="姓名"
                    required
                    placeholder="請輸入您的姓名"
                    className="w-full px-4 py-3 rounded-xl border border-[#ddd5c8] bg-[#faf8f5] text-[#3a2e22] text-sm placeholder-[#b0a090] focus:outline-none focus:border-[#b08060] transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="trial-phone" className="block text-[0.875rem] font-medium text-[#3a2e22] mb-2">
                    聯絡電話 <span className="text-[#c0503a]">*</span>
                  </label>
                  <input
                    id="trial-phone"
                    type="tel"
                    name="聯絡電話"
                    required
                    placeholder="09xx-xxx-xxx"
                    className="w-full px-4 py-3 rounded-xl border border-[#ddd5c8] bg-[#faf8f5] text-[#3a2e22] text-sm placeholder-[#b0a090] focus:outline-none focus:border-[#b08060] transition-colors"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="trial-email" className="block text-[0.875rem] font-medium text-[#3a2e22] mb-2">
                  電子信箱
                </label>
                <input
                  id="trial-email"
                  type="email"
                  name="email"
                  placeholder="your@email.com（選填）"
                  className="w-full px-4 py-3 rounded-xl border border-[#ddd5c8] bg-[#faf8f5] text-[#3a2e22] text-sm placeholder-[#b0a090] focus:outline-none focus:border-[#b08060] transition-colors"
                />
              </div>

              {/* Course Type */}
              <div>
                <label id="trial-course-label" className="block text-[0.875rem] font-medium text-[#3a2e22] mb-3">
                  希望報名的課程類型 <span className="text-[#c0503a]">*</span>
                </label>
                <div role="group" aria-labelledby="trial-course-label" className="flex flex-wrap gap-2.5">
                  {courseTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setSelectedCourse(type)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        selectedCourse === type
                          ? 'bg-[#5a3e22] text-white border-[#5a3e22]'
                          : 'bg-[#faf8f5] text-[#5a4e42] border-[#ddd5c8] hover:border-[#b08060] hover:text-[#7a6248]'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
                {/* Hidden required input */}
                <input type="hidden" name="課程類型" value={selectedCourse} required />
              </div>

              {/* Learning Background */}
              <div>
                <label htmlFor="trial-background" className="block text-[0.875rem] font-medium text-[#3a2e22] mb-2">
                  學習背景
                </label>
                <select
                  id="trial-background"
                  name="學習背景"
                  className="w-full px-4 py-3 rounded-xl border border-[#ddd5c8] bg-[#faf8f5] text-[#3a2e22] text-sm focus:outline-none focus:border-[#b08060] transition-colors cursor-pointer"
                >
                  <option value="">請選擇</option>
                  <option value="完全沒有音樂基礎">完全沒有音樂基礎</option>
                  <option value="有學過其他樂器">有學過其他樂器</option>
                  <option value="學過古箏但已中斷">學過古箏但已中斷</option>
                  <option value="目前仍在學習古箏">目前仍在學習古箏</option>
                  <option value="是家長，為孩子詢問">是家長，為孩子詢問</option>
                </select>
              </div>

              {/* Preferred Time */}
              <div>
                <label id="trial-time-label" className="block text-[0.875rem] font-medium text-[#3a2e22] mb-3">
                  方便上課的時段（可複選）
                </label>
                <div role="group" aria-labelledby="trial-time-label" className="flex flex-wrap gap-2.5">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => toggleTime(slot)}
                      className={`px-4 py-2 rounded-full text-sm border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        selectedTimes.includes(slot)
                          ? 'bg-[#f0e8dc] text-[#7a6248] border-[#c8b49a] font-medium'
                          : 'bg-[#faf8f5] text-[#5a4e42] border-[#ddd5c8] hover:border-[#c8b49a]'
                      }`}
                    >
                      {selectedTimes.includes(slot) && <i className="ri-check-line mr-1 text-xs"></i>}
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="trial-note" className="block text-[0.875rem] font-medium text-[#3a2e22] mb-2">
                  想先讓老師知道的事
                </label>
                <textarea
                  id="trial-note"
                  name="想先讓老師知道的事"
                  rows={4}
                  maxLength={500}
                  placeholder="例如：我平常很忙，希望排週末；或是我有一些疑問想先了解……（選填）"
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 rounded-xl border border-[#ddd5c8] bg-[#faf8f5] text-[#3a2e22] text-sm placeholder-[#b0a090] focus:outline-none focus:border-[#b08060] transition-colors resize-none"
                />
                <p className="text-right text-[0.75rem] text-[#b0a090] mt-1">{charCount}/500</p>
              </div>

              {/* Privacy note */}
              <p className="text-[0.8125rem] leading-[1.65] text-[#9a8878]">
                <i className="ri-lock-line mr-1"></i>
                您填寫的資料僅用於課程安排，不會外洩或用於其他目的。
              </p>

              {/* Error */}
              {status === 'error' && (
                <p role="alert" className="text-[0.875rem] text-[#c0503a] flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {errorMsg || '送出失敗，請稍後再試或直接加入 LINE@ 聯絡。'}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'submitting' || !selectedCourse}
                className={`w-full py-4 rounded-xl text-[1.0625rem] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  status === 'submitting' || !selectedCourse
                    ? 'bg-[#c8b49a] text-white cursor-not-allowed'
                    : 'bg-[#5a3e22] hover:bg-[#3a2e22] text-white'
                }`}
              >
                {status === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>送出中…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-calendar-check-line"></i>送出體驗課申請
                  </span>
                )}
              </button>
            </form>
          )}

          {/* Alternative CTA */}
          <div className="mt-8 text-center">
            <p className="text-[0.9375rem] text-[#7a6e62] mb-3">或者直接加 LINE@ 和我聊聊</p>
            <a
              href="https://line.me"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2.5 px-7 py-3 rounded-full border border-[#c8b49a] text-[#5a4e42] hover:bg-[#f5f0e8] text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-line-fill text-[#06c755] text-lg"></i>
              加入 LINE@ 諮詢
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}